'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS, getAiProficiencyTimeRangeQueryKey } from '@/domains/pages/ai-proficiency/query-options';
import { buildModelSavingsActualTierAdditionalGroupBy, buildModelSavingsOptimalTierAdditionalGroupBy, buildModelSavingsTopicGroupBy, modelSavingsTierMixPagination, MODEL_SAVINGS_TASKS_ORDER_BY, MODEL_SAVINGS_TASK_VARIABLES, MODEL_SAVINGS_TIER_MIX_VARIABLES, type ModelSavingsTasksQueryScope } from '@/domains/pages/model-savings/utils/model-savings-api';
import { mapModelSavingsTasks, taskTierGroupRowsByTask, taskTierGroupRowsToMixByTask } from '@/domains/pages/model-savings/utils/map-model-savings-aggregates';
import type { SavingsTask } from '@/domains/pages/model-savings/types';
import { useMemo } from 'react';
type UseModelSavingsTasksQueryParams = ModelSavingsTasksQueryScope & {
  enabled: boolean;
  limit: number;
  offset: number;
  queryKeySuffix: string;
};
export type ModelSavingsTasksQueryResult = {
  tasks: SavingsTask[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};
export function useModelSavingsTasksQuery(t0) {
  const $ = _c(80);
  const {
    projectId,
    timeRange,
    scopeFilters,
    enabled,
    limit,
    offset,
    queryKeySuffix
  } = t0;
  let t1;
  if ($[0] !== timeRange) {
    t1 = getAiProficiencyTimeRangeQueryKey(timeRange);
    $[0] = timeRange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timeRangeQueryKey = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = buildModelSavingsTopicGroupBy();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const topicGroupByPayload = t2;
  let t3;
  if ($[3] !== limit || $[4] !== offset) {
    t3 = modelSavingsTierMixPagination(limit, offset);
    $[3] = limit;
    $[4] = offset;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const tierMixPagination = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== limit || $[8] !== offset || $[9] !== projectId || $[10] !== scopeFilters || $[11] !== timeRange) {
    t5 = {
      project_id: projectId,
      time_range: timeRange,
      filters: t4,
      filters_v2: scopeFilters,
      group_by: topicGroupByPayload,
      order_by: MODEL_SAVINGS_TASKS_ORDER_BY,
      limit,
      offset
    };
    $[7] = limit;
    $[8] = offset;
    $[9] = projectId;
    $[10] = scopeFilters;
    $[11] = timeRange;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const sharedQueryParams = t5;
  let t6;
  if ($[13] !== limit || $[14] !== offset || $[15] !== projectId || $[16] !== queryKeySuffix || $[17] !== scopeFilters || $[18] !== timeRangeQueryKey) {
    t6 = [QUERY_KEY_INTERACTION_AGGREGATES, "model-savings-tasks", queryKeySuffix, projectId, timeRangeQueryKey, "topic", scopeFilters, limit, offset];
    $[13] = limit;
    $[14] = offset;
    $[15] = projectId;
    $[16] = queryKeySuffix;
    $[17] = scopeFilters;
    $[18] = timeRangeQueryKey;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== sharedQueryParams) {
    t7 = t8 => {
      const {
        signal
      } = t8;
      return getInteractionAggregates({
        ...sharedQueryParams,
        variables: MODEL_SAVINGS_TASK_VARIABLES
      }, {
        signal
      });
    };
    $[20] = sharedQueryParams;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== enabled || $[23] !== t6 || $[24] !== t7) {
    t8 = {
      queryKey: t6,
      queryFn: t7,
      enabled,
      keepPreviousData: true,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[22] = enabled;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const query = useQuery(t8);
  let t9;
  if ($[26] !== limit || $[27] !== offset || $[28] !== projectId || $[29] !== queryKeySuffix || $[30] !== scopeFilters || $[31] !== timeRangeQueryKey) {
    t9 = [QUERY_KEY_INTERACTION_AGGREGATES, "model-savings-task-actual-mix", queryKeySuffix, projectId, timeRangeQueryKey, "topic", scopeFilters, limit, offset];
    $[26] = limit;
    $[27] = offset;
    $[28] = projectId;
    $[29] = queryKeySuffix;
    $[30] = scopeFilters;
    $[31] = timeRangeQueryKey;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== sharedQueryParams || $[34] !== tierMixPagination) {
    t10 = t11 => {
      const {
        signal: signal_0
      } = t11;
      return getInteractionAggregates({
        ...sharedQueryParams,
        variables: MODEL_SAVINGS_TIER_MIX_VARIABLES,
        additional_group_bys: [buildModelSavingsActualTierAdditionalGroupBy()],
        limit: tierMixPagination.limit,
        offset: tierMixPagination.offset
      }, {
        signal: signal_0
      });
    };
    $[33] = sharedQueryParams;
    $[34] = tierMixPagination;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== enabled || $[37] !== t10 || $[38] !== t9) {
    t11 = {
      queryKey: t9,
      queryFn: t10,
      enabled,
      keepPreviousData: true,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[36] = enabled;
    $[37] = t10;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const actualMixQuery = useQuery(t11);
  let t12;
  if ($[40] !== limit || $[41] !== offset || $[42] !== projectId || $[43] !== queryKeySuffix || $[44] !== scopeFilters || $[45] !== timeRangeQueryKey) {
    t12 = [QUERY_KEY_INTERACTION_AGGREGATES, "model-savings-task-optimal-mix", queryKeySuffix, projectId, timeRangeQueryKey, "topic", scopeFilters, limit, offset];
    $[40] = limit;
    $[41] = offset;
    $[42] = projectId;
    $[43] = queryKeySuffix;
    $[44] = scopeFilters;
    $[45] = timeRangeQueryKey;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== sharedQueryParams || $[48] !== tierMixPagination) {
    t13 = t14 => {
      const {
        signal: signal_1
      } = t14;
      return getInteractionAggregates({
        ...sharedQueryParams,
        variables: MODEL_SAVINGS_TIER_MIX_VARIABLES,
        additional_group_bys: [buildModelSavingsOptimalTierAdditionalGroupBy()],
        limit: tierMixPagination.limit,
        offset: tierMixPagination.offset
      }, {
        signal: signal_1
      });
    };
    $[47] = sharedQueryParams;
    $[48] = tierMixPagination;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== enabled || $[51] !== t12 || $[52] !== t13) {
    t14 = {
      queryKey: t12,
      queryFn: t13,
      enabled,
      keepPreviousData: true,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[50] = enabled;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  const optimalMixQuery = useQuery(t14);
  let t15;
  if ($[54] !== actualMixQuery.data) {
    t15 = actualMixQuery.data ? taskTierGroupRowsToMixByTask(actualMixQuery.data.data) : new Map();
    $[54] = actualMixQuery.data;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  const actualMixByTask = t15;
  let t16;
  if ($[56] !== optimalMixQuery.data) {
    t16 = optimalMixQuery.data ? taskTierGroupRowsToMixByTask(optimalMixQuery.data.data) : new Map();
    $[56] = optimalMixQuery.data;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  const optimalMixByTask = t16;
  let t17;
  if ($[58] !== actualMixQuery.data) {
    t17 = actualMixQuery.data ? taskTierGroupRowsByTask(actualMixQuery.data.data) : new Map();
    $[58] = actualMixQuery.data;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  const actualTierRowsByTask = t17;
  let t18;
  if ($[60] !== optimalMixQuery.data) {
    t18 = optimalMixQuery.data ? taskTierGroupRowsByTask(optimalMixQuery.data.data) : new Map();
    $[60] = optimalMixQuery.data;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  const optimalTierRowsByTask = t18;
  let t19;
  if ($[62] !== actualMixByTask || $[63] !== actualTierRowsByTask || $[64] !== optimalMixByTask || $[65] !== optimalTierRowsByTask || $[66] !== query.data) {
    t19 = query.data ? mapModelSavingsTasks(query.data, actualMixByTask, optimalMixByTask, actualTierRowsByTask, optimalTierRowsByTask) : [];
    $[62] = actualMixByTask;
    $[63] = actualTierRowsByTask;
    $[64] = optimalMixByTask;
    $[65] = optimalTierRowsByTask;
    $[66] = query.data;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  const tasks = t19;
  const isLoading = query.isLoading || actualMixQuery.isLoading || optimalMixQuery.isLoading;
  const isFetching = query.isFetching || actualMixQuery.isFetching || optimalMixQuery.isFetching;
  const isError = query.isError || actualMixQuery.isError || optimalMixQuery.isError;
  const error = query.error ?? actualMixQuery.error ?? optimalMixQuery.error;
  let t20;
  if ($[68] !== actualMixQuery || $[69] !== optimalMixQuery || $[70] !== query) {
    t20 = () => {
      query.refetch();
      actualMixQuery.refetch();
      optimalMixQuery.refetch();
    };
    $[68] = actualMixQuery;
    $[69] = optimalMixQuery;
    $[70] = query;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  const refetch = t20;
  const t21 = query.data?.total ?? 0;
  let t22;
  if ($[72] !== error || $[73] !== isError || $[74] !== isFetching || $[75] !== isLoading || $[76] !== refetch || $[77] !== t21 || $[78] !== tasks) {
    t22 = {
      tasks,
      total: t21,
      isLoading,
      isFetching,
      isError,
      error,
      refetch
    };
    $[72] = error;
    $[73] = isError;
    $[74] = isFetching;
    $[75] = isLoading;
    $[76] = refetch;
    $[77] = t21;
    $[78] = tasks;
    $[79] = t22;
  } else {
    t22 = $[79];
  }
  return t22;
}